import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useAccess, Capability } from '../../../hooks/useAccess';
import { tierOf } from '../../../utils/roleTier';
import { APIErrorException } from '../../../services/types/errors';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '../../ui/form';

// Full user interface from the API
interface ApiUser {
  user_id: string;
  username: string;
  name?: string | null;
  email: string;
  organization_id: string;
  role: string;
  rbac_role?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// User data for form submissions. Note: no organization field — the target org
// is fixed by the caller's context server-side, never chosen in the form.
interface UserFormData {
  user_id?: string;
  name: string;
  // Legacy unique handle: sent unchanged on edit, backfilled from email on
  // create (login is by email).
  username?: string;
  email: string;
  password?: string;
  role: string;
  is_active?: boolean;
}

interface V3UserFormProps {
  user?: ApiUser;
  onSubmit: (userData: UserFormData) => Promise<void>;
  onClose: () => void;
  // How the copy names the organisation the user is added to.
  firmName: string;
  // The organisation's email domain, for the example address; null when unknown.
  emailDomain: string | null;
}

export const NETWORK_FAILURE = "We couldn't reach Good Outcomes. Try again.";

const buildSchema = (emailMessage: string) =>
  z.object({
    name: z.string().trim().min(1, 'Enter their full name.'),
    email: z.string().trim().min(1, emailMessage).email(emailMessage),
    password: z.string().optional(),
    role: z.string(),
    is_active: z.boolean().optional(),
  });

type FormValues = z.infer<ReturnType<typeof buildSchema>>;

const V3UserForm: React.FC<V3UserFormProps> = ({ user, onSubmit, onClose, firmName, emailDomain }) => {
  const { can } = useAccess();
  const isPlatformAdmin = can(Capability.PlatformManage);
  // Mirrors the server's rule: only a platform admin may assign the privileged
  // legacy 'admin' role — offering it to a firm admin just guarantees a 403.
  const canAssignAdmin = isPlatformAdmin;
  const isEditMode = !!user?.user_id;
  // The API refuses a password from anyone but a platform admin on create.
  const showPassword = isEditMode || isPlatformAdmin;
  const [loading, setLoading] = useState(false);

  const schema = useMemo(
    () => buildSchema(`Enter a full email address, like name@${emailDomain || 'company.com'}.`),
    [emailDomain],
  );

  // The org isn't loaded or shown: it is fixed at creation and never editable here.
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: user
      ? {
          name: user.name || '',
          email: user.email,
          password: '',
          role: tierOf(user.rbac_role, user.role),
          is_active: user.is_active !== false,
        }
      : { name: '', email: '', password: '', role: 'user' },
  });
  const role = form.watch('role');

  const handleSubmit = async (values: FormValues) => {
    setLoading(true);
    form.clearErrors('root');
    const password = values.password?.trim() ? values.password : undefined;
    try {
      await onSubmit({
        ...(isEditMode
          ? { user_id: user!.user_id, username: user!.username, is_active: values.is_active }
          : {}),
        name: values.name.trim(),
        email: values.email.trim(),
        role: values.role,
        ...(password && showPassword ? { password } : {}),
      });
    } catch (err) {
      if (err instanceof APIErrorException && err.status === 409 && !isEditMode) {
        form.setError('email', { type: 'server', message: `Someone in ${firmName} already uses this email.` });
      } else if (err instanceof APIErrorException) {
        form.setError('root', { type: 'server', message: err.error.message });
      } else {
        form.setError('root', { type: 'server', message: NETWORK_FAILURE });
      }
      console.error(err);
      setLoading(false);
    }
  };

  const rootError = form.formState.errors.root?.message;

  // Not dismissible mid-request, so the outcome lands on a form still open.
  return (
    <Dialog open onOpenChange={(open) => { if (!open && !loading) onClose(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Edit User' : 'Add user'}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5" noValidate>
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Full name</FormLabel>
                  <FormControl>
                    <Input autoComplete="off" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Work email</FormLabel>
                  <FormControl>
                    <Input type="email" autoComplete="off" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {showPassword && (
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{isEditMode ? 'Password' : 'Password (optional)'}</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        autoComplete="new-password"
                        placeholder={isEditMode ? 'Leave blank to keep current password' : 'Leave blank'}
                        {...field}
                      />
                    </FormControl>
                    {!isEditMode && (
                      <FormDescription>Leave blank and we'll email them a link to set their own password.</FormDescription>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Role</FormLabel>
                  {/* appearance-none + a chevron inset from the right edge so the
                      arrow isn't jammed against the border (native arrows sit too
                      far right). pr-10 keeps the value clear of the chevron. */}
                  <div className="relative">
                    <FormControl>
                      <select
                        {...field}
                        className="w-full appearance-none rounded-md border border-input bg-background px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-ring/30"
                      >
                        <option value="viewer">Viewer</option>
                        <option value="user">User</option>
                        {(canAssignAdmin || role === 'admin') && (
                          <option value="admin" disabled={!canAssignAdmin}>
                            Admin
                          </option>
                        )}
                      </select>
                    </FormControl>
                    <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  </div>
                </FormItem>
              )}
            />
            {isEditMode && (
              <FormField
                control={form.control}
                name="is_active"
                render={({ field }) => (
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={!!field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
                      Active account
                    </label>
                  </div>
                )}
              />
            )}

            {rootError && (
              <p role="alert" className="text-sm text-destructive">{rootError}</p>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {isEditMode ? (loading ? 'Saving...' : 'Update User') : 'Add user'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default V3UserForm;
